<template>
  <div>
    <input name="start" type="hidden" :value="data.start" />
    <input name="end" type="hidden" :value="data.end" />
    <v-dialog v-model="open" scrollable persistent max-width="640px">
      <v-card class="c-edit-event">
        <!-- Header -->
        <v-card-title>
          <select-community-header
            ref="cid"
            v-model="data.cid"
            :title="
              reading ? $T('attributes.event') : isEdit ? $T('event.edit_event_title') : $T('event.create_btn_title')
            "
            :fixCommunity="isEdit || isTalkFixed"
            :icon="reading ? 'mdi-calendar-blank' : 'mdi-calendar-edit'"
          ></select-community-header>
        </v-card-title>

        <v-divider></v-divider>
        <v-card-text>
          <template v-if="share">
            <div style="margin-bottom: 10px;">
              <selected-members
                v-if="share.tid"
                class="c-mems"
                :cid="data.cid"
                :cIcon="true"
                :memberGroups="shareTargets"
                :includeInactive="false"
                iconColor="clr-black"
                type="talk"
                :typeId="share.tid"
                :hideNoMemberMsg="true"
                @memberRequested="setShareTargets"
              ></selected-members>
            </div>
          </template>
          <div v-else-if="isTalkFixed" style="margin-bottom: 10px;">
            <selected-members
              class="c-mems"
              :cid="data.cid"
              :cIcon="true"
              :memberGroups="members"
              :includeInactive="false"
              iconColor="clr-black"
              type="talk"
              :typeId="talkShare.tid"
              :hideNoMemberMsg="true"
            ></selected-members>
          </div>
          <select-groups-members-field
            v-else
            ref="members"
            v-model="data.participants"
            attribute="participant"
            :cid="data.cid"
            :isEdit="isEdit"
            :creatorCmid="event ? event.creator_cmid : cidToCmid(data.cid)"
            :excludeSelf="false"
            :inviteOnly="inviteOnly"
            :defaultSelectedCmids="defaultSelectedCmids"
            :noSelectOk="true"
            @defaultValueSet="onDefaultValueSet"
          />
          <v-flex>
            <template v-if="reading">
              <div :class="isViewTitleBlank ? 'c-title-blank' : 'c-title'">
                {{ isViewTitleBlank ? `( ${$T('common.notitle')} )` : this.calEvent.item.title }}
              </div>
            </template>
            <template v-else>
              <v-text-field
                v-model="data.title"
                name="title"
                ref="title"
                :label="$T('common.title')"
                v-validate="'max:100'"
                :error-messages="formErrors.title"
              ></v-text-field>
            </template>
          </v-flex>

          <template v-if="reading">
            <tempLink v-if="!isViewRepeat">
              <div class="flex-row-left" style="margin-top: 10px;">
                <div style="margin-right: 5px;">
                  <v-icon>access_time</v-icon>
                </div>
                <div class="s c-time-c" v-html="fromTo"></div>
              </div>
            </tempLink>
          </template>
          <template v-else>
            <v-flex>
              <datetime-picker
                v-model="data.start"
                :dateOnly="data.allDay"
                :dateLabel="$T('attributes.start')"
                :timeLabel="''"
              ></datetime-picker>
              <div class="input-group__details" v-show="'start' in formErrors">
                <div class="input-group__messages input-group__error error--text">{{ formErrors.start }}</div>
              </div>
            </v-flex>
            <v-flex class="c-end-area">
              <datetime-picker
                v-model="data.end"
                :dateOnly="data.allDay"
                :dateLabel="$T('attributes.end')"
                :timeLabel="''"
              ></datetime-picker>

              <div class="input-group__details" v-if="'end' in formErrors">
                <div class="input-group__messages input-group__error error--text">{{ formErrors.end[0] }}</div>
              </div>
            </v-flex>
            <v-flex class="c-allday-area">
              <v-switch
                v-model="data.allDay"
                class="c-allday"
                color="clr-primary"
                :label="$T('common.all_day')"
              ></v-switch>
            </v-flex>
          </template>

          <v-flex class="c-repeat-area" :class="{ norepeat: !isRepeat }">
            <template v-if="!reading">
              <v-btn v-if="!reading" class="c-rpt-btn" flat color="clr-primary" @click="openRepeatDialog"
                ><v-icon>mdi-loop</v-icon>{{ $T('page.cal.repeat.btn') }}</v-btn
              >
            </template>
            <div
              class="c-rpt-detail"
              :style="reading ? 'margin-left:12px; margin-bottom: -10px; margin-top:50px;' : ''"
            >
              <div v-if="repeat.type == 'dates'">
                <datetime-picker
                  class="c-dp"
                  v-model="viewDates"
                  dateOnly
                  readOnly
                  noIcon
                  :dateLabel="$T('page.cal.repeat.days_tgts')"
                  multiple
                ></datetime-picker>
              </div>
              <div v-else-if="repeat.type == 'week'">
                <table class="c-week-cal">
                  <tr>
                    <td v-for="i in 7" class="c-week-td">
                      <div class="c-week-t">{{ weekdayKey(i - 1) }}</div>
                    </td>
                  </tr>
                  <tr>
                    <td v-for="i in 7">
                      <div class="c-week-c">
                        <v-checkbox v-model="viewWeeks" color="clr-primary" :value="`w${i - 1}`" disabled></v-checkbox>
                      </div>
                    </td>
                  </tr>
                </table>
              </div>
              <div v-if="isRepeat" class="c-rpt-msg">
                {{ repeatMsg }}
              </div>
              <div v-if="hasRepeatEnd" class="c-rpt-range">
                <div class="c-rpt-range-ttl">{{ $T('page.cal.repeat.range') }}</div>
                <div>
                  {{
                    `${repeat.start.substring(0, 10)} - ${
                      repeat.end ? repeat.end.substring(0, 10) : $T('common.endless')
                    }`
                  }}
                </div>
              </div>
            </div>
          </v-flex>

          <template v-if="!reading">
            <v-flex v-if="!isEdit || event.is_creator" class="c-editable-area">
              <v-checkbox v-model="data.editable" :label="$T('event.editable')" color="clr-primary"></v-checkbox>
            </v-flex>
          </template>

          <template v-if="reading">
            <v-flex v-if="calEvent.item.location">
              <div class="c-flex as c-loc" @click="goLocation(calEvent.item.location)">
                <v-icon color="black">location_on</v-icon>
                <div class="s c-loc-c">{{ event.location }}</div>
              </div>
            </v-flex>
            <v-flex v-if="calEvent.item.desc" class="js as w100">
              <div class="c-doc" v-html="formatExplain(calEvent.item.desc)"></div>
            </v-flex>
          </template>
          <template v-else>
            <div v-show="!detail">
              <v-flex class="c-detail-area">
                <v-btn flat small color="clr-primary" @click="detail = true">{{ $T('common.input_detail') }}</v-btn>
              </v-flex>
            </div>
            <div v-show="detail">
              <v-flex class="c-loc-area">
                <v-text-field
                  v-model="data.location"
                  id="location"
                  name="location"
                  prepend-icon="location_on"
                  :label="$T('event.location')"
                ></v-text-field>
              </v-flex>
              <v-flex class="c-desc-area">
                <v-textarea
                  v-model="data.desc"
                  name="desc"
                  :label="$T('common.description')"
                  multi-line
                  v-validate="'max:10000'"
                  :error-messages="formErrors.desc"
                  counter="10000"
                ></v-textarea>
              </v-flex>
            </div>
          </template>
        </v-card-text>
        <v-divider></v-divider>

        <!-- Footer -->
        <v-card-actions>
          <v-spacer></v-spacer>
          <template v-if="reading">
            <v-btn v-if="editable" color="clr-btn" flat @click.native="onStartEdit">{{ $T('common.edit') }}</v-btn>
            <v-btn color="clr-btn-cancel" flat @click.native="$emit('close')">{{ $T('button.close') }}</v-btn>
          </template>
          <template v-else>
            <v-btn color="clr-btn" flat @click.native="onConfirmed" :loading="loading" :disabled="loading">{{ $T('button.save') }}</v-btn>
            <v-btn color="clr-btn-cancel" flat @click.native="$emit('close')" :disabled="loading">{{ $T('button.cancel') }}</v-btn>
          </template>
          <v-spacer></v-spacer>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <repeat-dialog
      ref="repeatDialog"
      v-model="data.repeat"
      :start="data.start"
      :end="data.end"
      :allDay="data.allDay"
      :hasRepeatFromEditStart="hasRepeatFromEditStart"
    ></repeat-dialog>

    <v-dialog v-model="updateRepeatDialog" max-width="400px">
      <v-card>
        <!-- Header -->
        <v-card-title>
          <div class="c-flex js ac w100">
            <div class="c-title s">
              {{ $T('page.cal.repeat.edit.title') }}
            </div>
            <v-icon small class="c-close" :disabled="loading" @click="updateRepeatDialog = false">close</v-icon>
          </div>
        </v-card-title>

        <v-divider></v-divider>
        <v-card-text>
          <v-flex class="c-msg">{{ $T('page.cal.repeat.edit.confirm') }}</v-flex>
        </v-card-text>
        <v-divider></v-divider>

        <!-- Footer -->
        <v-card-actions>
          <v-container grid-list-md text-xs-center class="c-action-container">
            <v-layout row wrap>
              <v-flex xs12>
                <v-btn
                  slot="activator"
                  round
                  dark
                  color="clr-primary"
                  @click.prevent="execUpdate(true)"
                  class="w100"
                  :loading="loading && data.affectAll"
                  :disabled="loading"
                >
                  {{ $T('page.cal.repeat.edit.to_all') }}
                </v-btn>
              </v-flex>
              <v-flex xs12>
                <v-btn
                  slot="activator"
                  round
                  dark
                  color="clr-primary"
                  @click.prevent="execUpdate(false)"
                  class="w100 c-btn-tail"
                  :loading="loading && !data.affectAll"
                  :disabled="loading"
                >
                  {{ $T('page.cal.repeat.edit.to_this') }}
                </v-btn>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import CacheUtils from '../../utils/CacheUtils';
import datetimePicker from '../common/DateTimePicker.vue';
import repeatDialog from './EditEventRepeatDialog.vue';
import { fetchCommunityMembers, fetchCommunityManagers } from '../../api/communityMember';
import { createEvent, editEvent, getEvent, getShareMembers } from '../../api/communityEvent';
import { addMsgEvent } from '../../api/communityTalk';
import { COMMIN } from '../../constant';

export default {
  components: {
    'datetime-picker': datetimePicker,
    'repeat-dialog': repeatDialog,
  },
  props: {
    calEvent: { type: Object, required: false },
    readOnly: { type: Boolean, required: false, default: false },
    initCid: { type: Number, required: false },
    members: { type: Array, required: false },
    targetMoment: { type: Object, required: false },
    // When set ({tid}) the created event is shared into the talk (addMsgEvent) — talk-only.
    talkShare: { type: Object, required: false },
  },
  created() {
    this.setShareTargets(this.calEvent);
  },
  data() {
    return {
      data: {
        cid: this.calEvent ? this.calEvent.item.community_id : this.initCid ? this.initCid : this.getRouteCid(),
        title: this.calEvent ? this.calEvent.item.title : null,
        start: this.toInitStart(this.calEvent),
        end: this.toInitEnd(this.calEvent),
        allDay: this.calEvent ? !!this.calEvent.item.all_day : false,
        editable: this.calEvent ? !!this.calEvent.item.editable : false,
        desc: this.calEvent ? this.calEvent.item.desc : null,
        location: this.calEvent ? this.calEvent.item.location : null,
        participants: this.inviteOnly ? [] : this.members,
        // event unique id (like 'd20190215')
        uid: this.calEvent ? this.calEvent.uid : null,
        repeat: this.toInitRepeat(this.calEvent),
        // flag for repat
        affectAll: true,
      },
      reading: this.readOnly,
      viewWeeks: [],
      viewDates: [],
      tmpWeeks: null,
      tmpDates: null,
      updateRepeatDialog: false,
      detail: !!this.event,
      open: true,
      formErrors: {},
      repeatDialog: false,
      repeatMsg: null,
      shareTargets: [],
      loading: false,
    };
  },
  beforeDestroy() {
    this.$store.commit('CAL_DIALOG_EDIT', false);
  },
  mounted() {
    if (open) this.$store.commit('CAL_DIALOG_EDIT', true);
    if (this.data.uid) {
      // when update remove self only first time.
      var dates = this.repeat.dates;
      if (dates) {
        var v = this.$moment(this.data.start).format('YYYY-MM-DD');
        var i = dates.indexOf(v);
        if (i != -1) dates.splice(i, 1);
      }
    }
    this.recalcStart(this.repeat, this.data.start);

    var self = this;
    setTimeout(function() {
      if (!self.data.cid) self.$refs.cid.focus();
      if (!self.isTalkFixed && (!self.data.participants || self.data.participants.length == 0))
        self.$refs.members.focus();
      if (!self.reading) self.$refs.title.focus();
    }, 500);

    var setWatch = setInterval(function() {
      if (!self.$refs.repeatDialog) return;
      self.repeatMsg = self.$refs.repeatDialog.repeatMsg;
      self.$watch('$refs.repeatDialog.repeatMsg', function(n, v) {
        self.repeatMsg = n;
      });
      clearInterval(setWatch);
    }, 50);
  },
  watch: {
    open(n, o) {
      this.$store.commit('CAL_DIALOG_EDIT', n);
      if (!n) {
        this.$emit('close');
      }
    },
    selectedMembers(val) {
      this.participants = val.join();
    },
    'data.repeat': function(n, o) {
      this.recalcStart(n, this.data.start);
    },
    'data.allDay': function(n, o) {
      if (n) return;
      var d = this.data;
      if (this.isValidStartEnd(d.start, d.end, n)) return;
      var s = this.$moment(d.start);
      d.start = s.format('YYYY-MM-DD HH:mm');
      d.end = s.add(60, 'm').format('YYYY-MM-DD HH:mm');
    },
    'data.start': function(n, o) {
      this.recalcStart(this.repeat, n);
      var s = n.substring(0, 10);
      if (!this.repeat.rangeSet || s < this.repeat.start) {
        this.repeat.start = s;
      }
      if (this.repeat.end && (!this.repeat.rangeSet || s > this.repeat.end)) {
        this.repeat.end = s;
      }

      var d = this.data;
      if (this.isValidStartEnd(n, d.end, d.allDay)) return;
      if (d.allDay) {
        d.end = this.$moment(n).format('YYYY-MM-DD 00:00:00');
      } else {
        d.end = this.$moment(n)
          .add(60, 'm')
          .format('YYYY-MM-DD HH:mm');
      }
    },
    'data.end': function(n, o) {
      var d = this.data;
      if (this.isValidStartEnd(d.start, n, d.allDay)) return;
      if (d.allDay) {
        d.start = this.$moment(n).format('YYYY-MM-DD 00:00:00');
      } else {
        d.start = this.$moment(n)
          .add(-60, 'm')
          .format('YYYY-MM-DD HH:mm');
      }
    },
  },
  computed: {
    event() {
      return this.calEvent ? this.calEvent.item : null;
    },
    isEdit() {
      return !!this.event;
    },
    // Creating from a talk: participants are fixed to the talk members (not configurable);
    // the event is registered with participants = ['t{tid}'] and shared via addMsgEvent.
    isTalkFixed() {
      return !this.isEdit && !!this.talkShare && !!this.talkShare.tid;
    },
    repeat() {
      return this.data.repeat;
    },
    isRepeat() {
      return this.repeat.type != 'none';
    },
    hasRepeatEnd() {
      return ['none', 'dates'].indexOf(this.repeat.type) == -1;
    },
    hasRepeatFromEditStart() {
      return this.event && this.event.repeat && this.event.repeat.type ? this.event.repeat.type != 'none' : false;
    },
    inviteOnly() {
      return this.event && !this.event.is_creator;
    },
    defaultSelectedCmids() {
      return this.inviteOnly && this.members ? this.members : [];
    },
    share() {
      if (!this.calEvent || !this.calEvent.item) return null;
      return this.calEvent.item.share;
    },
    editable() {
      return !this.calEvent || !!this.calEvent.item.is_creator || !!this.calEvent.item.editable;
    },
    isViewTitleBlank() {
      return this.calEvent && this.calEvent.item.title && this.calEvent.item.title.trim() == '';
    },
    isViewRepeat() {
      return this.calEvent && this.calEvent.item.repeat.type != 'none';
    },
    fromTo() {
      const event = this.calEvent.item;
      var s = this.$moment.unix(event.start);
      var e = this.$moment.unix(event.end);
      if (event.all_day) {
        if (this.isSameDay(s, e)) return this.toYmd(s);
        if (this.leTbS) return `<div>${this.toYmd(s)} -<div><div>${this.toYmd(e)}</div>`;
        return `${this.toYmd(s)} - ${this.toYmd(e)}`;
      }
      if (this.isSameDay(s, e)) return `${this.toYmdHm(s)} - ${this.toHm(e)}`;
      if (this.leTbS) return `<div>${this.toYmdHm(s)} -</div><div>${this.toYmdHm(e)}</div>`;
      return `${this.toYmdHm(s)} - ${this.toYmdHm(e)}`;
    },
  },
  methods: {
    goLocation(loc) {
      if (!loc) return;
      // window.open(this.$utils.toMapUrl(loc), '_gmap');
      window.open(this.$utils.toMapUrl(loc));
    },
    formatExplain(desc) {
      return formatExplain(desc);
    },
    recalcStart(repeat, startDate) {
      this.recalcViewDates(repeat, startDate);
      this.recalcViewWeeks(repeat, startDate);
    },
    recalcViewDates(repeat, startDate) {
      var n = [];
      if (repeat.dates) Array.prototype.push.apply(n, repeat.dates);
      var t = startDate.substring(0, 10);
      if (n.indexOf(t) == -1) n.push(t);
      n.sort();
      var r = this.viewDates;
      r.splice(0, r.length);
      Array.prototype.push.apply(r, n);
    },
    recalcViewWeeks(repeat, startDate) {
      var n = [];
      if (repeat.weeks) Array.prototype.push.apply(n, repeat.weeks);
      var wkey = 'w' + this.$moment(startDate).weekday();
      if (n.indexOf(wkey) == -1) n.push(wkey);
      var r = this.viewWeeks;
      r.splice(0, r.length);
      Array.prototype.push.apply(r, n);
    },
    toInitStart(calEvent) {
      if (!calEvent) return this.toFormattedDate(30);
      return calEvent.startD;
    },
    toInitEnd(calEvent) {
      if (!calEvent) return this.toFormattedDate(60);
      return calEvent.endD;
    },
    toInitRepeat(calEvent) {
      if (!calEvent) return this.defaultRepeat();
      var event = calEvent.item;
      if (!event.repeat || !event.repeat.type || event.repeat.type == 'none') return this.defaultRepeat();

      var repeat = Object.assign({}, event.repeat);
      var s = repeat.start ? this.$moment.unix(repeat.start) : this.$moment.unix(event.start);
      var e = repeat.end ? this.$moment.unix(repeat.end) : null;
      if (e && e.isAfter(this.$moment('3000-01-01 00:00:00'))) e = null;
      if (event.all_day) {
        s.tz('UTC');
        if (e) e.tz('UTC');
      }
      repeat.start = s.format('YYYY-MM-DD');
      repeat.end = e ? e.format('YYYY-MM-DD') : null;

      var type = repeat.type;
      repeat.rangeSet = 'dates' != repeat.type;
      if ('dates' == type) {
        var utcOffset = this.$moment().utcOffset();
        var dates = [];
        for (var d in event.dates) {
          if (event.dates[d] != 1) continue;
          if (event.all_day) {
            dates.push(this.$moment(d.substring(1), 'YYYYMMDD').format('YYYY-MM-DD'));
          } else {
            var tmpD = this.$moment(d.substring(1), 'YYYYMMDD');
            tmpD.add(repeat.startUtcMin + utcOffset, 'm');
            dates.push(tmpD.format('YYYY-MM-DD'));
          }
        }
        repeat.dates = dates;
      } else if ('week' == type) {
        if (!event.all_day) {
          var utcOffset = this.$moment().utcOffset();
          var add = 0;
          if (60 * 24 - utcOffset < repeat.startUtcMin) add = 1;
          else if (repeat.startUtcMin < -utcOffset) add = -1;

          if (add != 0) {
            var newWeeks = [];
            for (var w of repeat.weeks) {
              var i = Number(w.substring(1));
              i += add;
              i = i < 0 ? 6 : i > 6 ? 0 : i;
              newWeeks.push(`w${i}`);
            }
            repeat.weeks = newWeeks;
          }
        }

        var dates = [];
        for (var d in event.dates) {
          if (event.dates[d] != 1) continue;
          if (event.all_day) {
            dates.push(this.$moment(d.substring(1), 'YYYYMMDD').format('YYYY-MM-DD'));
          } else {
            var tmpD = this.$moment(d.substring(1), 'YYYYMMDD');
            tmpD.add(repeat.startUtcMin + utcOffset, 'm');
            dates.push(tmpD.format('YYYY-MM-DD'));
          }
        }
        repeat.dates = dates;
      }
      return repeat;
    },
    defaultRepeat() {
      return {
        rangeSet: false,
        type: 'none',
        start: null,
        end: null,
        registerEnd: null,
      };
    },
    toComminMoment(v) {
      if (Number.isInteger(v)) return this.$moment.unix(v);
      if (typeof v === 'string' || v instanceof String) return this.$moment(v, 'YYYY-MM-DD HH:mm');
      // not use unix because timezone will be defferent.
      return this.$moment(v.format('YYYYMMDDHHmm'), 'YYYYMMDDHHmm');
    },
    toFormattedDate(addMin) {
      var t = this.targetMoment ? this.$moment(this.targetMoment) : this.$moment();
      t = t.add(addMin, 'm');
      var mm = Number(t.format('m')) >= 30 ? '30' : '00';
      return this.$moment(t.format('YYYY-MM-DD HH:') + mm + ':00').format('YYYY-MM-DD HH:mm');
    },
    initFocus() {
      if (this.isEdit) {
        this.$refs.title.focus();
      } else {
        if (this.isAllCommunitySelected) {
          this.$refs.cid.focus();
        } else {
          this.$refs.title.focus();
        }
      }
    },
    isValidStartEnd(s, e, allDay) {
      if (!s || !e) return false;
      if (allDay) {
        var ms = Number(this.$moment(s).format('YYYYMMDD'));
        var me = Number(this.$moment(e).format('YYYYMMDD'));
        return ms <= me;
      }
      if (s.length != 16 || e.length != 16) return false;
      var ms = this.$moment(s);
      var me = this.$moment(e);
      return ms.isBefore(me);
    },
    addFormErrors(error) {
      this.formErrors = [];
      console.log(error);
      let errs = error.errors;
      for (var k in errs) {
        this.formErrors[k] = errs[k];
      }
    },
    onStartEdit() {
      this.reading = false;
    },
    onConfirmed() {
      this.loading = true;

      // When fixed to a talk the participants picker is replaced by a read-only display (no ref).
      var valid =
        (this.calEvent || this.$refs.cid.isValid()) &&
        (!!this.share || this.isTalkFixed || this.$refs.members.isValid());

      var self = this;
      this.$validator.validateAll().then(result => {
        if (!valid || !result) return (self.loading = false);
        if (self.isEdit) {
          self.update();
        } else {
          self.register();
        }
      });
    },
    // When opened from a talk (talkShare={tid}), share the created event into the talk as an
    // event-reference message and surface it in the open chat (Chat.vue watches talk.latest).
    shareEventToTalk(newEvent) {
      if (!this.talkShare || !this.talkShare.tid || !newEvent || !newEvent.id) return;
      const tid = this.talkShare.tid;
      const data = new FormData();
      data.append('tid', tid);
      data.append('eid', newEvent.id);
      addMsgEvent(data)
        .then(response => {
          this.$store.commit('TALK_UPDATE_LATEST', { id: tid, d: { latest: response.data } });
        })
        .catch(e => {
          console.error('failed to share event to talk', e);
        });
    },
    async register() {
      try {
        this.setDataForRegister();
        var d = Object.assign({}, this.data);
        if (this.isTalkFixed) {
          // Fix participants to the talk members (a snapshot of the current talk member codes).
          // The app sends ['t{tid}'], but the Java event-participant resolution expects member
          // codes, so we send the resolved talk member keys (fall back to self if empty).
          d.participants = this.members && this.members.length ? this.members : [`m${this.cidToCmid(d.cid)}`];
        } else if (!d.participants || d.participants.length == 0) {
          d.participants = [`m${this.cidToCmid(d.cid)}`];
        }
        const response = await createEvent(setupFormData(d));
        this.resetDataFromRegister();
        const newEvent = response.data.event;
        CacheUtils.setEvent(newEvent.id, newEvent);
        this.$store.commit('CAL_APPEND_REGISTERED', { item: newEvent });
        this.shareEventToTalk(newEvent);
        this.$emit('registered', this.data.start);
        this.open = false;
        this.loading = false;
      } catch (e) {
        this.resetDataFromRegister();
        this.addFormErrors(e);
        this.loading = false;
        console.error('failed to register data', e);
        this.errRegisterData();
      }
    },
    update() {
      if (this.calEvent && !this.readOnly && this.calEvent.item.is_creator && this.hasRepeatFromEditStart) {
        // Pause the in-flight flag while the user picks all/this in the confirm dialog
        // (execUpdate sets it true again); otherwise the default-true affectAll pre-spins a button.
        this.loading = false;
        this.updateRepeatDialog = true;
        return;
      }
      this.execUpdate(true);
    },
    async execUpdate(affectAll) {
      try {
        this.loading = true;
        this.setDataForRegister();
        this.data.affectAll = affectAll;
        const data = { ...this.data };
        if (this.share) {
          data.participants = [`m${this.cidToCmid(this.calEvent.item.community_id)}`];
        }
        const response = await editEvent(this.event.id, setupFormData(data));
        this.resetDataFromRegister();
        const updatedEvent = response.data.updated.event;
        CacheUtils.setEvent(updatedEvent.id, updatedEvent);
        this.$store.commit('CAL_APPEND_UPDATED', { old: this.event, item: updatedEvent });
        if (response.data.new) {
          const newEvent = response.data.new.event;
          CacheUtils.setEvent(newEvent.id, newEvent);
          this.$store.commit('CAL_APPEND_REGISTERED', { item: newEvent });
        }
        this.open = false;
        this.loading = false;
      } catch (e) {
        this.resetDataFromRegister();
        this.addFormErrors(e);
        this.loading = false;
        console.error('failed to edit data', e);
        this.errEditData();
      }
    },
    setDataForRegister() {
      if (this.data.start.length == 10) this.data.start += ' 00:00';
      if (this.data.end.length == 10) this.data.end += ' 00:00';
      if (this.tmpWeeks) return;
      this.tmpWeeks = this.data.repeat.weeks;
      this.data.repeat.weeks = this.viewWeeks;
      this.tmpDates = this.data.repeat.dates;
      this.data.repeat.dates = this.viewDates;

      var repeat = this.data.repeat;
      if (!repeat.end) {
        repeat.registerEnd = null;
      } else {
        var e = this.$moment(repeat.end, 'YYYY-MM-DD HH:mm');
        repeat.registerEnd = e.format('YYYY-MM-DD');
      }
    },
    resetDataFromRegister() {
      if (!this.tmpWeeks) return;
      this.data.repeat.weeks = this.tmpWeeks;
      this.tmpWeeks = null;
      this.data.repeat.dates = this.tmpDates;
      this.tmpDates = null;
    },
    onDefaultValueSet() {
      this.$refs.title.focus();
    },
    openRepeatDialog() {
      this.$refs.repeatDialog.openDialog(this.viewWeeks, this.viewDates);
    },
    weekdayKey(idx) {
      var m = this.$moment().startOf('week');
      if (idx > 0) {
        m = m.add(idx, 'day');
      }
      return m.format('ddd');
    },
    async setShareTargets(calEvent) {
      if (!calEvent) calEvent = this.calEvent;
      if (!calEvent || !calEvent.item.share) {
        return;
      }
      const event = calEvent.item;
      this.shareTargets = await this.getShareTargets(event.id);
    },
    async getShareTargets(eid) {
      try {
        const cmids = await getShareMembers(eid);
        const ids = [];
        for (const cmid of cmids) ids.push(`m${cmid}`);
        return ids;
      } catch (e) {
        console.error('failed to get data', e);
        this.errGetData();
        return null;
      }
    },
    isSameDay(a, b) {
      return a.format('YYYYMMDD') == b.format('YYYYMMDD');
    },
    toYmd(m) {
      var f =
        this.userCountry == 'us' && this.userLang == 'en' ? $T('common.moment.us.ymdw') : $T('common.moment.ymdw');
      return m.format(f);
    },
    toHm(m) {
      return m.format('HH:mm');
    },
    toYmdHm(m) {
      return `${this.toYmd(m)} ${this.toHm(m)}`;
    },
  },
};
</script>

<style scoped>
.c-end-area {
  margin-top: -10px;
}
.c-allday-area {
  margin-top: -25px;
}
.c-repeat-area {
  margin: -25px 0 36px -10px;
}
.c-repeat-area.norepeat {
  margin: -25px 0 15px -10px;
}
.c-repeat-area i {
  font-size: 15px;
  margin: -1px 3px 0 0;
}
.c-detail-area {
  margin-top: -10px;
}
.c-editable-area {
  margin-top: -10px;
}
.c-loc-area {
  margin-top: -15px;
}
.c-desc-area {
  margin-top: -5px;
}
.c-allday {
  width: 100%;
}
.c-dlg-icon {
  margin-right: 5px;
}
.input-group__details {
  margin: -10px 0 30px 0;
}
.c-rpt-btn {
  margin-top: 10px;
}
.c-rpt-detail {
  margin-left: 42px;
  font-size: 16px;
}
.c-rpt-none {
  color: rgba(0, 0, 0, 0.54);
}
.c-week-cal {
  max-width: 320px;
}
.c-week-td {
  position: relative;
}
.c-week-t {
  font-size: 14px;
  position: absolute;
  left: 12px;
  top: 0;
  transform: translateX(-50%);
}
.c-week-c {
  margin-bottom: -20px;
}
.c-rpt-range {
  margin-top: 5px;
}
.c-rpt-range-ttl {
  font-size: 12px;
}
.c-dp {
  margin: -5px 0 10px 0;
}
.c-action-container {
  padding: 3px 20px 5px 20px;
}

.c-title {
  font-size: 20px;
  font-weight: 600;
  color: black;
}
.c-title-blank {
  font-size: 20px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.37);
}
.c-loc {
  color: black;
  margin-bottom: 20px;
  cursor: hand;
  cursor: pointer;
}
.c-loc-c {
  font-size: 15px;
  font-weight: 600;
}
.c-doc {
  color: black;
  font-size: 14px;
  margin-bottom: 20px;
  margin-right: 8px;
  border-radius: 5px;
  padding: 12px;
  border: inset 1px lightgrey;
}
</style>
